<template>
  <div>
    <ParagraphBody :class="$style.toolBarText">
      {{ getHeadingText }}
    </ParagraphBody>
    <ToolbarContainer>
      <template v-slot:toolbar>
        <nav>
          <div :class="$style.gridContainerOuter">
            <div :class="$style.gridItemOuter">
              <InputSearchFilter
                :class="$style.filterHeight"
                placeholder="Search"
                @apply="handleFilterDeviceApply"
                @clearAll="handleClearAll"
                @updateSearchTerm="handleUpdateSearchTerm"
              >
                <template v-slot:content>
                  <SelectChipGroup
                    v-model="selectedDeviceFilter"
                    label="Device Status:"
                    name="choiceChips"
                    :options="filterOptions"
                  />
                </template>
              </InputSearchFilter>
            </div>
            <div :class="$style.gridContainerInner">
              <Checkbox
                :class="[$style.gridItemInner, $style.marginLeftCheckBox]"
                :isHelperTextMarkdown="false"
                :isMarkdown="false"
                :label="boundedDeviceLabel"
                @input="handleBoundItemsInputClick"
              />
            </div>
          </div>
        </nav>
      </template>
      <template v-slot:body>
        <ItemListPagination
          :class="$style.tableContainer"
          columnGap=""
          :columns="columns"
          :currentPageNum="currentPageNum"
          :isLoading="isListLoading"
          :itemComponent="$options.GoogleAppsDeviceRow"
          :rowsPerPage="rowsPerPage"
          :selectAll="selectAll"
          selectable
          :showEmptyState="showEmptyState"
          :sortBy="sortBy"
          :sortDirection="sortDirection"
          :tableData="tableData"
          :totalCount="totalCount"
          @pageChange="handlePageChange"
          @select="handleSelect"
          @selectAll="handleSelectAll"
          @sortColumn="handleSortColumn"
        />
        <ButtonGroupSave
          :class="$style.saveButton"
          :isFullWidth="false"
          :primaryButtonProps="primaryButtonProps"
          :secondaryButtonProps="secondaryButtonProps"
          @primaryAction="handleSave"
          @secondaryAction="handleClose"
        />
      </template>
    </ToolbarContainer>
  </div>
</template>

<script>
import {
  ButtonGroupSave,
  Checkbox,
  Constants,
  InputSearchFilter,
  ItemListPagination,
  ParagraphBody,
  SelectChipGroup,
  ToolbarContainer,
  Types,
} from '@jumpcloud/ui-components';
import {
  ItemTypeConsts, StoreModelMap,
  StoreModuleMap,
} from 'client/util/Constants';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import GoogleAppsDeviceRow from 'client/SoftwareApps/GoogleAppsDeviceRow.vue';
import SoftwareAppsApi from 'client/api/v2/SoftwareAppsApi';
import SystemFilters from 'components/ItemTable/CollectionFilterComponents/constants/SystemFilters';

const { Column, PrimaryButtonProps, SecondaryButtonProps } = Types;
const { sortDirections } = Constants;
const sortByDefault = 'displayName';
const sortDirectionDefault = sortDirections.ascending;
const systemCollection = StoreModuleMap[ItemTypeConsts.system];
const softwareAppsModel = StoreModelMap[ItemTypeConsts.softwareApps];

const appColumns = [
  Column({
    dataFieldName: 'status',
    label: 'Status',
    sortable: true,
    width: '',
    visible: true,
  }),
  Column({
    dataFieldName: 'displayName',
    label: 'Device Name',
    sortable: true,
    width: '',
    visible: true,
  }),
  Column({
    dataFieldName: 'os',
    label: 'OS',
    sortable: true,
    width: '',
    visible: true,
  }),
];

export default {
  name: 'GoogleAppsDevice',

  components: {
    ButtonGroupSave,
    Checkbox,
    InputSearchFilter,
    ItemListPagination,
    ParagraphBody,
    SelectChipGroup,
    ToolbarContainer,
  },

  data() {
    return {
      boundedDeviceCount: 0,
      boundedDeviceLabel: '',
      columns: appColumns,
      currentPageNum: 0,
      filterOptions: [{ name: 'active', value: true }, { name: 'inactive', value: false }],
      isListLoading: true,
      manageSoftCount: '',
      rowsPerPage: 10,
      selectAll: false,
      selectedDeviceFilter: [],
      selectedDeviceItemTable: [],
      associationDeviceChangeItemTable: [],
      showEmptyState: false,
      sortBy: sortByDefault,
      sortDirection: sortDirectionDefault,
      sortField: `${sortDirectionDefault === sortDirections.ascending ? '' : '-'}${sortByDefault}`,
      tableData: [],
    };
  },

  GoogleAppsDeviceRow,

  computed: {
    ...mapGetters(systemCollection, [
      'getCollection',
      'getBoundItemsTotalCount',
      'getBoundItem',
      'getItem',
      'areShowingBoundItems',
    ]),

    ...mapGetters(softwareAppsModel, [
      'displayName',
    ]),

    ...mapState(systemCollection, {
      stateCount: (state) => state.entities.items.totalCount,
    }),

    getHeadingText() {
      return `${this.displayName} is deployed in the following devices`;
    },

    primaryButtonProps() {
      return PrimaryButtonProps({
        text: 'save',
      });
    },

    secondaryButtonProps() {
      return SecondaryButtonProps({
        text: 'cancel',
      });
    },

    totalCount() {
      return this.stateCount;
    },
  },

  mounted() {
    this.handleMounted();
  },

  methods: {
    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    ...mapActions(systemCollection, [
      'fetchCollection',
      'resetCollection',
      'selectAllItems',
      'setSortField',
      'setPage',
      'setFilter',
      'setFilters',
      'setSearchTerm',
      'showBoundItems',
      'showAllItems',
      'setShowBoundItems',
      'setBoundItemsUrl',
    ]),

    async getDeviceBoundItemsCount() {
      await this.resetCollection();
      const str = window.location.href;
      const myArray = str.split('/');
      const appId = myArray[6];
      this.setBoundItemsUrl({ url: `/api/v2/softwareapps/${appId}/systems` });
      this.setShowBoundItems(true);
      const filter = SystemFilters.osFamily('android');
      this.setFilter({ filter });
      await this.fetchCollection();
      this.boundedDeviceLabel = `Show Bounded Devices (${this.getBoundItemsTotalCount})`;
      this.boundedDeviceCount = this.getBoundItemsTotalCount;
      this.getCollection.forEach((item) => {
        this.selectedDeviceItemTable.push(item.id);
      });
      this.setShowBoundItems(false);
      await this.resetCollection();
    },

    async handleBoundItemsInputClick(checked) {
      await this.resetCollection();
      const filter = SystemFilters.osFamily('android');
      this.setFilter({ filter });
      this.setPage({ pageNumber: this.currentPageNum, limit: this.rowsPerPage });
      this.setShowBoundItems(checked);
      await this.refreshData();
    },

    async handleClearAll() {
      this.selectedDeviceFilter = [];
      this.isListLoading = true;
      await this.resetCollection();
      const filter = SystemFilters.osFamily('android');
      this.setFilter({ filter });
      this.setPage({ pageNumber: this.currentPageNum, limit: this.rowsPerPage });
      this.refreshData();
    },

    async handleClose() {
      this.associationDeviceChangeItemTable = [];
      const filter = SystemFilters.osFamily('android');
      this.setFilter({ filter });
      this.setPage({ pageNumber: this.currentPageNum, limit: this.rowsPerPage });
      await this.refreshData();
      this.selectAll = false;
    },

    async handleFilterDeviceApply() {
      await this.resetCollection();
      this.isListLoading = true;
      const filterList = [];
      filterList.push(SystemFilters.osFamily('android'));
      this.selectedDeviceFilter.forEach(filter => {
        filterList.push(SystemFilters.status(filter.value));
      });
      this.setFilters({ filtersArray: filterList });
      this.setPage({ pageNumber: this.currentPageNum, limit: this.rowsPerPage });
      await this.refreshData();
    },

    async handleMounted() {
      await this.getDeviceBoundItemsCount();
      const filter = SystemFilters.osFamily('android');
      this.setFilter({ filter });
      this.setPage({ pageNumber: this.currentPageNum, limit: this.rowsPerPage });
      await this.refreshData();
    },

    async handlePageChange({ currentPageNum, rowsPerPage }) {
      this.isListLoading = true;
      await this.resetCollection();
      const filter = SystemFilters.osFamily('android');
      this.setFilter({ filter });
      this.currentPageNum = currentPageNum;
      this.rowsPerPage = rowsPerPage;
      this.setPage({ pageNumber: currentPageNum, limit: rowsPerPage });
      await this.refreshData();
    },

    handleSave() {
      const str = window.location.href;
      const myArray = str.split('/');
      const appId = myArray[6];
      let itrIndex = 0;
      this.associationDeviceChangeItemTable.forEach(async (item) => {
        try {
          if (item.selected && this.selectedDeviceItemTable.indexOf(item.id) === -1) {
            const params = {
              op: 'add',
              type: 'system',
              id: item.id,
              attributes: null,
            };
            await SoftwareAppsApi.association(appId, params);
            this.boundedDeviceCount += 1;
            this.selectedDeviceItemTable.push(item.id);
            this.triggerSuccessNotification({
              title: `Success: ${item.id}`,
              message: 'Association successfull',
            });
          } else if (!item.selected && this.selectedDeviceItemTable.indexOf(item.id) !== -1) {
            const params = {
              op: 'remove',
              type: 'system',
              id: item.id,
              attributes: null,
            };
            await SoftwareAppsApi.association(appId, params);
            this.boundedDeviceCount -= 1;
            this.selectedDeviceItemTable.splice(this.selectedDeviceItemTable.indexOf(item.id), 1);
            this.triggerSuccessNotification({
              title: `Success: ${item.id}`,
              message: 'Dissociation successfull',
            });
          }
        } catch (error) {
          const message = get(error, 'response.data.message', '');
          this.triggerErrorNotification({
            title: `Error: ${item.id}`,
            message,
          });
        }
        itrIndex += 1;
        if (itrIndex === this.associationDeviceChangeItemTable.length) {
          await this.resetCollection();
          const filter = SystemFilters.osFamily('android');
          this.setFilter({ filter });
          this.setPage({ pageNumber: this.currentPageNum, limit: this.rowsPerPage });
          await this.refreshData();
          this.boundedDeviceLabel = `Show Bounded Devices (${this.boundedDeviceCount})`;
          this.associationDeviceChangeItemTable = [];
        }
      });
    },

    handleSelect(selectedItem) {
      const foundItemIndex = this.tableData.findIndex((item) => item.id === selectedItem.id);
      this.tableData[foundItemIndex].selected = !this.tableData[foundItemIndex].selected;

      const foundAssociatedItemIndex = this.associationDeviceChangeItemTable.findIndex(
        (item) => item.id === selectedItem.id,
      );
      if (foundAssociatedItemIndex === -1) {
        this.associationDeviceChangeItemTable.push(this.tableData[foundItemIndex]);
      } else {
        // eslint-disable-next-line max-len
        this.associationDeviceChangeItemTable[foundItemIndex].selected = !this.associationDeviceChangeItemTable[foundItemIndex].selected;
      }
    },

    handleSelectAll(value) {
      this.associationDeviceChangeItemTable = [];
      for (let i = 0; i < this.tableData.length; i += 1) {
        this.tableData[i].selected = value;
        this.associationDeviceChangeItemTable.push(this.tableData[i]);
      }

      this.selectAll = true;
    },

    async handleSortColumn({ dataFieldName, sortDirection }) {
      this.isListLoading = true;
      this.sortDirection = sortDirection;
      this.sortBy = dataFieldName;
      this.sortField = `${this.sortDirection === sortDirections.ascending ? '-' : ''}${dataFieldName}`;
      this.setSortField({ sortField: this.sortField });
      this.setPage({ pageNumber: this.currentPageNum, limit: this.rowsPerPage });
      await this.refreshData();
    },

    handleUpdateSearchTerm(value) {
      this.isListLoading = true;
      this.resetCollection();
      this.setSearchTerm({ searchTerm: value, preventReset: false });
      this.setPage({ pageNumber: this.currentPageNum, limit: this.rowsPerPage });
      this.refreshData();
    },

    async refreshData() {
      try {
        if (this.areShowingBoundItems) {
          const str = window.location.href;
          const myArray = str.split('/');
          const appId = myArray[6];
          this.setBoundItemsUrl({ url: `/api/v2/softwareapps/${appId}/systems` });
        }
        await this.fetchCollection();
      } catch (error) {
        this.triggerErrorNotification({
          title: 'Error',
          message: 'There is a problem fetching devices.',
        });
      } finally {
        this.isListLoading = false;
        this.tableData = [];
        this.getCollection.forEach((item) => {
          let selected = false;
          if (this.selectedDeviceItemTable.includes(item.id)) {
            selected = true;
          }
          this.tableData.push({
            ...item,
            selected,
          });
        });
        this.showEmptyState = (this.totalCount === 0);
      }
    },
  },
};
</script>

<style module>
.gridContainerOuter {
  display: flex;
  margin-bottom: 0.75rem;
  margin-left: var(--jc-spacer-small);
  margin-top: 0.75rem;
}

.gridContainerInner {
  display: flex;
  flex-wrap: wrap;
  margin-left: var(--jc-spacer);
  margin-right: var(--jc-spacer);
}

.gridItemOuter {
  flex: 1 1 auto;
  margin-left: var(--jc-spacer-x-small);
}

.gridItemInner {
  align-items: center;
  display: flex;
  justify-content: center;
}

.tableContainer {
  padding: 0 var(--jc-spacer) 0.75rem var(--jc-spacer);
}

.tableContainer footer {
  bottom: 0;
  margin-bottom: 3.625rem;
  right: 0;
}

.filterHeight {
  margin-left: var(--jc-spacer-x-small);
}

.filterHeight > div > div:nth-child(2) > div > div:nth-child(2) {
  min-height: 6.375rem;
}

.marginLeftCheckBox.marginLeftCheckBox {
  margin-left: var(--jc-spacer);
  margin-top: var(--jc-spacer-x-small);
}

.toolBarText {
  border-bottom: var(--jc-border);
  margin-bottom: var(--jc-spacer-x-small) !important;
  margin-left: 1.25rem !important;
  margin-right: 1.25rem !important;
  padding: var(--jc-spacer) var(--jc-spacer-x-small);
}

.saveButton {
  border: var(--jc-border);
  bottom: 0;
  padding-bottom: var(--jc-spacer);
  padding-right: var(--jc-spacer);
  padding-top: var(--jc-spacer);
  position: fixed;
  right: 0;
  width: 100%;
}
</style>
